import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Destination d'accueil unique selon le rôle — partagée par Root, Login, AuthCallback et Cockpit.
export const homeFor = (role) =>
  role === "chauffeur" ? "/chauffeur"
    : ["client_entreprise", "passager", "partner"].includes(role) ? "/portail"
      : "/cockpit";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sidi_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);
  // booting : vrai tant que le rôle n'a pas été revérifié via /auth/me au démarrage.
  const [booting, setBooting] = useState(!!localStorage.getItem("sidi_token") && !window.location.hash?.includes("session_id="));

  const setSession = (token, u) => {
    localStorage.setItem("sidi_token", token);
    localStorage.setItem("sidi_user", JSON.stringify(u));
    setUser(u);
  };

  const clearSession = () => {
    localStorage.removeItem("sidi_token");
    localStorage.removeItem("sidi_user");
    setUser(null);
  };

  // Au démarrage : si un token existe, on revérifie TOUJOURS le rôle via /auth/me.
  // Aucune redirection tant que la réponse n'est pas revenue. Échec => logout complet.
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setBooting(false); return; }
    const t = localStorage.getItem("sidi_token");
    if (!t) { setBooting(false); return; }
    let alive = true;
    api.get("/auth/me", { __skipRetry: false })
      .then(r => { if (!alive) return; setUser(r.data); localStorage.setItem("sidi_user", JSON.stringify(r.data)); })
      .catch(() => { if (alive) clearSession(); })
      .finally(() => { if (alive) setBooting(false); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    // On repart d'une session propre avant toute tentative.
    localStorage.removeItem("sidi_token");
    localStorage.removeItem("sidi_user");
    setUser(null);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user);
      return data.user;
    } catch (err) {
      if (err?.response?.status === 401) clearSession();
      throw err;
    } finally { setLoading(false); }
  };

  const logout = () => {
    api.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
    clearSession();
  };

  return <AuthCtx.Provider value={{ user, login, logout, loading, booting, setSession }}>{children}</AuthCtx.Provider>;
}
